import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { 
    flightSetupSchema, 
    type FlightSetupFormData, 
    type AOIOption,
    type UserOption,
} from '../types/flight.types';
import { DEFAULT_FLIGHT_VALUES } from '../constants';
import { useMissionsQuery } from '@/features/mission/api';
import { useAOIsQuery } from '@/features/aoi/api';
import { useOtherUsersQuery } from '@/features/user/api';

/**
 * Mission Option for select input
 */
export interface MissionOption {
    value: string;
    label: string;
}

/**
 * Return type for useFlightSetupForm hook
 */
export interface UseFlightSetupFormReturn {
    form: UseFormReturn<FlightSetupFormData>;
    missionOptions: MissionOption[];
    aoiOptions: AOIOption[];
    userOptions: UserOption[];
    hasRequiredErrors: boolean;
    isLoadingMissions: boolean;
    isLoadingAOIs: boolean;
    isLoadingUsers: boolean;
}

/**
 * Custom hook for Flight Setup form logic
 * Separates form state and configuration from UI
 */
export function useFlightSetupForm(): UseFlightSetupFormReturn {
    const form = useForm<FlightSetupFormData>({
        resolver: zodResolver(flightSetupSchema),
        mode: 'onChange',
        defaultValues: DEFAULT_FLIGHT_VALUES,
    });

    // Fetch missions from API
    const { data: missionsData, isLoading: isLoadingMissions } = useMissionsQuery({ size: 100 });

    // Fetch AOIs from API
    const { data: aoisData, isLoading: isLoadingAOIs } = useAOIsQuery({ size: 100 });

    // Fetch other users from API
    const { data: usersData, isLoading: isLoadingUsers } = useOtherUsersQuery();

    // Transform missions to options format
    const missionOptions: MissionOption[] = useMemo(() => {
        if (!missionsData?.content) return [];
        return missionsData.content.map((mission) => ({
            value: mission.missionId,
            label: mission.missionName,
        }));
    }, [missionsData?.content]);

    // Transform AOIs to options format
    const aoiOptions: AOIOption[] = useMemo(() => {
        if (!aoisData?.content) return [];
        return aoisData.content.map((aoi) => ({
            value: aoi.aoiId,
            label: aoi.aoiName,
        }));
    }, [aoisData?.content]);

    // Transform users to options format
    const userOptions: UserOption[] = useMemo(() => {
        if (!usersData) return [];
        return usersData.map((user) => ({
            value: user.userId,
            label: `${user.fullname} (${user.nrp})`,
        }));
    }, [usersData]);

    const { formState: { errors } } = form;
    const hasRequiredErrors = !!(errors.flightName || errors.selectMission || errors.selectAOIs || errors.selectPilot);

    return {
        form,
        missionOptions,
        aoiOptions,
        userOptions,
        hasRequiredErrors,
        isLoadingMissions,
        isLoadingAOIs,
        isLoadingUsers,
    };
}

